import { armySupply, unitSupply, deploymentProblem } from "@empire/shared";
import { useMemo } from "react";
import { create } from "zustand";
import {
  MAX_UNITS_PER_ATTACK,
  MAX_WAVES,
  MAX_WAVE_OFFSET_TICKS,
  TICKS_PER_SECOND,
  UNITS,
  checkPlanArmy,
  planCost,
  planSize,
  planUnits,
  unitStats,
  validatePlan,
  type AttackPlan,
  type UnitCounts,
  type UnitKind,
} from "@empire/core-sim";
import { useVillageStore } from "./village.js";

/**
 * Composing an army (S3-1).
 *
 * The planner holds counts per wave rather than the flat unit list the
 * simulation wants, because "seven Militia" is what a player edits and
 * ["militia" x7] is what the battle needs. `toPlan` is the one place that
 * converts, and the ordering it produces is what decides who walks in front —
 * so it is stable rather than dependent on object key order.
 */

/** The stepper moves entry times in half-seconds, per 03 §S11. */
export const OFFSET_STEP_TICKS = TICKS_PER_SECOND / 2;
/** Where a newly added wave starts, after the last one: 3 s. */
export const NEW_WAVE_GAP_TICKS = TICKS_PER_SECOND * 3;

export interface WaveDraft {
  /** Ticks after the first wave. Wave 1 is always 0. */
  offsetTicks: number;
  counts: Partial<Record<UnitKind, number>>;
}

interface PlanState {
  /**
   * An army to plan with instead of the village's — a Trial hands one out
   * (29 Sep 2026). Null: the units standing in the village (D-109).
   */
  army: UnitCounts | null;
  setArmy: (army: UnitCounts | null) => void;
  waves: WaveDraft[];
  /** Which wave the unit chips add to. */
  active: number;

  add: (kind: UnitKind, n?: number) => void;
  remove: (kind: UnitKind, n?: number) => void;
  setCount: (waveIndex: number, kind: UnitKind, count: number) => void;
  addWave: () => void;
  removeWave: (index: number) => void;
  setActive: (index: number) => void;
  nudgeOffset: (index: number, steps: number) => void;
  clear: () => void;
  /**
   * Trim the plan to the army actually at home, taking from the LAST waves
   * first. After a battle the planner kept its old plan — ten Militia with
   * seven left — and the Attack button sat disabled (friends test #2).
   */
  fitTo: (have: Partial<Record<UnitKind, number>>) => void;
}

const emptyWave = (offsetTicks = 0): WaveDraft => ({ offsetTicks, counts: {} });

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.trunc(value)));
}

export const usePlanStore = create<PlanState>((set, get) => ({
  army: null,
  setArmy: (army) => set({ army, waves: [emptyWave()], active: 0 }),
  waves: [emptyWave()],
  active: 0,

  add: (kind, n = 1) => {
    const { waves, active } = get();
    const current = waves[active]?.counts[kind] ?? 0;
    get().setCount(active, kind, current + n);
  },

  remove: (kind, n = 1) => {
    const { waves, active } = get();
    const current = waves[active]?.counts[kind] ?? 0;
    get().setCount(active, kind, current - n);
  },

  setCount: (waveIndex, kind, count) =>
    set((state) => {
      const current = state.waves[waveIndex]?.counts[kind] ?? 0;
      // Going UP is capped at what is standing in the village and at the
      // attack's unit limit, counted across every wave. The planner used to
      // take any number and only refuse at Send (playtest, 29 Sep 2026: "only
      // after you try to move on you receive an error"). Going down is never
      // refused, so a plan that is over (the army shrank) can always be fixed.
      let target = count;
      if (target > current) {
        const room = Math.min(
          affordable(state.waves, kind),
          MAX_UNITS_PER_ATTACK - planSize(toPlan(state.waves))
        );
        target = current + Math.max(0, Math.min(target - current, room));
        if (target === current) return state;
      }
      const waves = state.waves.map((wave, i) => {
        if (i !== waveIndex) return wave;
        const counts = { ...wave.counts };
        // Zero removes the key rather than storing a zero, so an empty wave is
        // actually empty and the "wave has no units" check stays honest.
        if (target <= 0) delete counts[kind];
        else counts[kind] = clamp(target, 1, MAX_UNITS_PER_ATTACK);
        return { ...wave, counts };
      });
      return { waves };
    }),

  addWave: () =>
    set((state) => {
      if (state.waves.length >= MAX_WAVES) return state;
      const last = state.waves[state.waves.length - 1]!;
      // A new wave lands a few seconds after the one before it — far enough
      // apart that the choice visibly matters (half a second was the old
      // default, and it read as one army). The stepper still moves in halves.
      const offsetTicks = Math.min(last.offsetTicks + NEW_WAVE_GAP_TICKS, MAX_WAVE_OFFSET_TICKS);
      return { waves: [...state.waves, emptyWave(offsetTicks)], active: state.waves.length };
    }),

  removeWave: (index) =>
    set((state) => {
      if (state.waves.length <= 1) return { waves: [emptyWave()], active: 0 };
      const waves = state.waves.filter((_, i) => i !== index);
      // Wave 1 always enters at 0. If the first wave was the one removed, the
      // new first wave becomes the reference point and everything shifts with
      // it — otherwise the whole attack would silently start late.
      const shift = waves[0]!.offsetTicks;
      return {
        waves: waves.map((w) => ({ ...w, offsetTicks: w.offsetTicks - shift })),
        active: clamp(state.active, 0, waves.length - 1),
      };
    }),

  setActive: (index) => set((state) => ({ active: clamp(index, 0, state.waves.length - 1) })),

  nudgeOffset: (index, steps) =>
    set((state) => {
      // Wave 1 is the reference point every other offset is measured from, so
      // it cannot be moved. Moving it would mean shifting all the others by the
      // same amount, which changes nothing and only looks like it did.
      if (index === 0) return state;
      return {
        waves: state.waves.map((wave, i) =>
          i === index
            ? {
                ...wave,
                offsetTicks: clamp(
                  wave.offsetTicks + steps * OFFSET_STEP_TICKS,
                  0,
                  MAX_WAVE_OFFSET_TICKS
                ),
              }
            : wave
        ),
      };
    }),

  clear: () => set({ waves: [emptyWave()], active: 0 }),

  fitTo: (have) => {
    const { waves } = get();
    const left: Partial<Record<UnitKind, number>> = { ...have };
    let changed = false;
    // Keep the earliest waves whole: they are the ones the player built first.
    const fitted = waves.map((wave) => ({ ...wave, counts: { ...wave.counts } }));
    for (const wave of fitted) {
      for (const kind of Object.keys(wave.counts) as UnitKind[]) {
        const want = wave.counts[kind] ?? 0;
        const can = Math.max(0, Math.min(want, left[kind] ?? 0));
        left[kind] = (left[kind] ?? 0) - can;
        if (can !== want) {
          changed = true;
          if (can === 0) delete wave.counts[kind];
          else wave.counts[kind] = can;
        }
      }
    }
    if (changed) set({ waves: fitted });
  },
}));

/** Drafts -> the shape the simulation and the server take. */
export function toPlan(waves: readonly WaveDraft[]): AttackPlan {
  return {
    waves: waves
      .filter((wave) => Object.keys(wave.counts).length > 0)
      .map((wave) => {
        const units: UnitKind[] = [];
        // Balance-sheet order, not object key order: the front of the column is
        // a real decision, and it must not depend on the order a player happened
        // to tap the chips.
        for (const unit of UNITS) {
          const kind = unit.id as UnitKind;
          for (let i = 0; i < (wave.counts[kind] ?? 0); i++) units.push(kind);
        }
        return { offsetTicks: wave.offsetTicks, units };
      }),
  };
}

export interface PlanSummary {
  readonly plan: AttackPlan;
  readonly cost: number;
  readonly size: number;
  readonly unitCap: number;
  readonly supply: number;
  readonly supplyCap: number;
  /** Null when the plan is launchable. */
  readonly problem: string | null;
  readonly empty: boolean;
}

/**
 * What the village has standing in it, for the planner to spend.
 *
 * Read from the village store rather than held here, because the army is the
 * village's, not the plan's: it changes when a unit finishes training and when
 * an attack comes home, and a second copy would be stale in exactly the moment
 * a player is deciding what to send. An empty object when the village has not
 * loaded yet — the planner then offers nothing, which is honest.
 */
export function heldArmy(): UnitCounts {
  return usePlanStore.getState().army ?? armyOf(useVillageStore.getState().economy);
}

/** The same thing, but the screen re-renders when a unit finishes training. */
export function useHeldArmy(): UnitCounts {
  const economy = useVillageStore((s) => s.economy);
  const override = usePlanStore((s) => s.army);
  // Derived with useMemo rather than inside the selector: the selector runs on
  // every store change and a fresh object each time would fail zustand's
  // equality check and re-render the planner forever.
  return useMemo(() => override ?? armyOf(economy), [economy, override]);
}

function armyOf(economy: { units: readonly { id: string; have: number }[] } | null): UnitCounts {
  if (!economy) return {};
  const counts: Partial<Record<UnitKind, number>> = {};
  for (const unit of economy.units) {
    if (unit.have > 0) counts[unit.id as UnitKind] = unit.have;
  }
  return counts;
}

/**
 * Everything the planner screen needs to know, in one pass.
 *
 * Both checks come from core-sim: `validatePlan` for what the simulation can
 * take, `checkPlanArmy` for whether these units exist. The client asks the same
 * two questions the server will, with the same functions, so the Launch button
 * is never offering something that would come back a 422 — which since D-109 is
 * the difference between "you have eleven Militia" and a refusal a player has
 * no way to have predicted.
 */
export function summarise(waves: readonly WaveDraft[], have: UnitCounts = heldArmy()): PlanSummary {
  const plan = toPlan(waves);
  const empty = plan.waves.length === 0;
  const cost = planCost(plan);
  const size = planSize(plan);

  let problem: string | null = null;
  if (empty) {
    problem = "Add some units.";
  } else {
    const shape = validatePlan(plan);
    if (!shape.ok) problem = shape.error.message;
    else {
      const held = checkPlanArmy(plan, have);
      if (!held.ok) problem = held.error.message;
    }
  }

  const supplyCap = usePlanStore.getState().army
    ? Infinity
    : (useVillageStore.getState().economy?.deploymentSupply ?? 28);
  if (!problem && Number.isFinite(supplyCap)) problem = deploymentProblem(plan, supplyCap);
  return {
    plan,
    cost,
    size,
    supply: armySupply(planUnits(plan)),
    supplyCap,
    unitCap: MAX_UNITS_PER_ATTACK,
    problem,
    empty,
  };
}

/** What one of these is worth. Shown on the chip beside what you own. */
export function unitPrice(kind: UnitKind): number {
  return unitStats(kind).totalCost;
}

/**
 * How many more of this unit there are left to add.
 *
 * Counted across EVERY wave, not the one being edited: five in the first wave
 * and five in the second is ten Militia leaving the village. Getting this wrong
 * would let the planner offer an army twice over and hand the refusal to the
 * server.
 */
export function affordable(
  waves: readonly WaveDraft[],
  kind: UnitKind,
  have: UnitCounts = heldArmy()
): number {
  const used = planUnits(toPlan(waves))[kind] ?? 0;
  if (unitStats(kind).combat === false) return 0;
  const room = usePlanStore.getState().army
    ? Infinity
    : Math.floor(
        ((useVillageStore.getState().economy?.deploymentSupply ?? 28) -
          armySupply(planUnits(toPlan(waves)))) /
          unitSupply(kind)
      );
  return Math.max(0, Math.min(room, (have[kind] ?? 0) - used));
}

export const secondsOf = (ticks: number): number => ticks / TICKS_PER_SECOND;
